import { Loading } from 'components/loading/Loading';
import SavedPaymentMethods from 'components/SavedPaymentMethods';
import React, { useEffect, useState } from 'react';
import { formatDate, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import UserAddresses from './components/UserAddresses';
import Tag from 'components/Tag';
import ActionButton from 'components/ActionButton';
import AddPaymentModal from 'components/AddPaymentModal';
import { removeRoleFromUser } from 'services/api/users';
import { addPaymentMethod } from 'services';
import Checkbox from 'components/FormFields/checkbox';
import useProfile from 'hooks/profile.hook';
import { validateEmail, validateName, validatePhone } from 'utils/validations';

const UserOverview = (props) => {
  const { loading: updateProfileLoading, updateUserFn } = useProfile();
  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userDetailsToEdit, setUserDetailsToEdit] = useState({}) as any;
  const {
    user,
    user: { notificationPreference = {}} = {},
    account,
    userAddresses,
    addressLoading,
    refetchAddresses,
    updateUser,
    setUser,
  } = props;

  useEffect(() => {
    setUserDetailsToEdit({
      phone: user?.phone,
      username: user?.username,
      email: user?.username,
      firstName: user?.firstName,
      lastName: user?.lastName,
    });
  }, [user]);
  const removeRole = async (role: string) => {
    setRoleLoading(true);
    try {
      const res = await removeRoleFromUser({ userId: user.objectId, role });
      if (res) {
        updateUser();
        showSuccess('role is removed successfully.');
      }
    } catch (e) {
      showError(e);
    } finally {
      setRoleLoading(false);
    }
  };

  const togglePaymentModal = () => setPaymentModalOpen(!paymentModalOpen);

  const onNewPaymentMethodAdded = async (token) => {
    try {
      await addPaymentMethod(token, user.objectId);
      showSuccess('New Payment Method added');
      setPaymentModalOpen(false);
      setReload(!reload);
    } catch (e) {
      showError(e);
    }
  };

  const getUserRolesSection = () => {
    if (!user.roles || !user.roles.length) return;
    return (
      <div className={styles['user-roles-section']}>
        <div className={styles['label']}>User Roles</div>
        <div className={styles['roles-card']}>
          {user.roles.map((role, i) => (
            <Tag key={i} tagName={role} onRemove={() => removeRole(role)} />
          ))}
        </div>
      </div>
    );
  };

  const getAccountDetails = () =>
    !!account && account.objectId ? (
      <div className={styles['account-section-container']}>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Account ID</div>
          <a
            href={`/accounts/${account.objectId}`}
            className={`${styles['detail']} ${styles['id']}`}
          >
            {account.objectId}
          </a>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Created At</div>
          <div className={styles['detail']}>
            {formatDate(account.createdAt)}
          </div>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Name</div>
          <div className={styles['detail']}>{account.name}</div>
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Phone</div>
          <div className={styles['detail']}>{account.phone}</div>
        </div>
        <div className={styles['address-group']}>
          <div className={styles['label']}>Address</div>
          <div className={styles['detail']}>{account.address}</div>
        </div>
      </div>
    ) : null;

  const getUserAddresses = () =>
    addressLoading ? (
      <Loading type={'mini'} />
    ) : (
      <UserAddresses
        user={user}
        addresses={userAddresses}
        refetchAddresses={refetchAddresses}
      />
    );

  const getPaymentMethods = () => (
    <div className={styles['payment-methods-container']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Payment Methods</div>
        <ActionButton
          text={
            <>
              Add<span className={'ml-3'}>+</span>
            </>
          }
          active={true}
          isLoading={false}
          onClick={togglePaymentModal}
        />
      </div>
      <SavedPaymentMethods user={user} reload={reload} />
    </div>
  );

  const validateSaveUserDetails = (): any => {
    if (!userDetailsToEdit?.username) {
      showError('Please enter Username');
      return false;
    }
    if (!userDetailsToEdit?.firstName) {
      showError('Please enter first name');
      return false;
    }
    if (!userDetailsToEdit?.lastName) {
      showError('Please enter last name');
      return false;
    }
    if (!userDetailsToEdit?.phone) {
      showError('Please enter Phone');
      return false;
    }
    if (!validateEmail(userDetailsToEdit?.username)) {
      showError('Please enter valid email');
      return false;
    }
    if (!validatePhone(userDetailsToEdit?.phone)) {
      showError('Please enter valid Phone');
      return false;
    }
    if (!validateName(userDetailsToEdit?.name)) {
      showError('Please enter valid Name');
      return false;
    }
    return true;
  };

  const updateUserDetails = async (params) => {
    const updatedUser = await updateUserFn({
      userId: user.objectId,
      ...params,
    });
    setUser(updatedUser);
    setIsEditMode(false);
  };

  const getInternalUser = () => {
    return (
      <div className={`${styles['details-group']}`}>
        <div className={`${styles['label']} mr-2`}>Internal User</div>
        <Checkbox
          title=""
          isChecked={user?.isInternalUser}
          setIsChecked={(e) => {
            updateUserDetails({ isInternalUser: e });
          }}
        />
      </div>
    );
  };

  const onClickSaveProfile = async (details) => {
    if (isEditMode) {
      const vaildStatus = validateSaveUserDetails();
      if (!vaildStatus) return;
      await updateUserDetails({ ...details, email: details.username });
    } else {
      setIsEditMode(!isEditMode);
    }
  };

  const getUserDetail = (params) => {
    const {
      title,
      value,
      customClass,
      editable = true,
      key,
      inputType = 'text',
    } = params;
    if (isEditMode && editable) {
      return (
        <div className={styles['details-group']}>
          <div className={styles['label']}>{title}</div>
          <input
            className={'form-control w-50'}
            type={inputType}
            value={userDetailsToEdit[key]}
            onChange={(e) => {
              setUserDetailsToEdit({
                ...userDetailsToEdit,
                [key]: e?.target?.value,
              });
            }}
          />
        </div>
      );
    }

    return (
      <div className={styles['details-group']}>
        <div className={styles['label']}>{title}</div>
        <div className={`${styles['detail']} ${customClass}`}>{value}</div>
      </div>
    );
  };
  const getBasicDetails = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['buttons-container']}>
        {isEditMode && (
          <ActionButton
            text={'Cancel'}
            active
            isLoading={false}
            onClick={() => {
              setIsEditMode(false);
            }}
            customClass={styles['cancel']}
          />
        )}
        <ActionButton
          text={isEditMode ? 'Save' : 'Edit'}
          active
          isLoading={false}
          onClick={() => {
            onClickSaveProfile(userDetailsToEdit);
          }}
        />
      </div>
      {getUserDetail({
        title: 'User ID',
        value: user.objectId,
        customClass: styles['id'],
        editable: false,
      })}
      {getUserDetail({
        title: 'First Name',
        value: user.firstName,
        key: 'firstName',
      })}
      {getUserDetail({
        title: 'Last Name',
        value: user.lastName,
        key: 'lastName',
      })}
      {getUserDetail({
        title: 'Username',
        value: user.username,
        key: 'username',
      })}
      {getUserDetail({
        title: 'Phone',
        value: user.phone,
        key: 'phone',
        inputType: 'number',
      })}
      {getUserDetail({
        title: 'IP Address',
        value: user.ipAddress || '-',
        key: 'ipAddress',
        editable: false,
      })}
      {getInternalUser()}
      {notificationPreferences()}
      {getUserRolesSection()}
    </div>
  );

  const notificationPreferences = () => {
    const abandonedCartNotiStatus =
      'abandonedCart' in notificationPreference
        ? notificationPreference?.abandonedCart
        : true;
    const suggestedCartNotiStatus =
      'suggestedCart' in notificationPreference
        ? notificationPreference?.suggestedCart
        : true;

    return (
      <div>
        Notification Preference
        <div className="d-flex">
          <div className={`${styles['details-group']}`}>
            <div className={`${styles['label']} mr-2`}>Abandoned Cart</div>
            <Checkbox
              title=""
              isChecked={abandonedCartNotiStatus}
              setIsChecked={(e) => {
                updateUserDetails({
                  notificationPreference: {
                    ...notificationPreference,
                    abandonedCart: e,
                  },
                });
              }}
              customClass="mt-2"
            />
          </div>
          <div className={`${styles['details-group']} ml-2`}>
            <div className={`${styles['label']} mr-2`}>Suggested Cart</div>
            <Checkbox
              title=""
              isChecked={suggestedCartNotiStatus}
              setIsChecked={(e) => {
                updateUserDetails({
                  notificationPreference: {
                    ...user?.notificationPreference,
                    suggestedCart: e,
                  },
                });
              }}
              customClass="mt-2"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {paymentModalOpen && (
        <AddPaymentModal
          onCancel={() => setPaymentModalOpen(false)}
          onNewPaymentMethodAdded={onNewPaymentMethodAdded}
        />
      )}
      {(roleLoading || updateProfileLoading) && <Loading />}
      {getBasicDetails()}
      {getPaymentMethods()}
      {getUserAddresses()}
      {getAccountDetails()}
    </div>
  );
};

export default UserOverview;
